import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useI18n } from '../lib/i18n';
import { formatWhen } from '../lib/format';
import { can, useSession } from '../lib/session';
import { useBookings, useCloseBooking } from '../lib/queries';
import { BookingForm, useDurationLabel } from './BookingForm';
import {
  Alert, Button, Card, EmptyState, Pill, SectionTitle, Skeleton,
} from './ui';
import type { Booking } from '../lib/types';
import { EraseButton } from './EraseButton';

/** The form in a card, for the list. */
function BookingCard({ booking, onDone }: { booking?: Booking; onDone: () => void }) {
  const { t } = useI18n();
  return (
    <Card>
      <SectionTitle>{booking ? t('booking.edit') : t('booking.new')}</SectionTitle>
      <BookingForm booking={booking} onDone={onDone} />
    </Card>
  );
}

function Row({ booking, mayWrite }: { booking: Booking; mayWrite: boolean }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const close = useCloseBooking();
  const durationLabel = useDurationLabel();
  const [editing, setEditing] = useState(false);

  if (editing) return <BookingCard booking={booking} onDone={() => setEditing(false)} />;

  const late = new Date(booking.expectedAt) < new Date();

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-display text-lg font-bold leading-tight text-ink">
              {booking.name}
            </span>
            {booking.loyaltyId && (
              <Link
                to={`/customers/${booking.customerId}`}
                className="tabular rounded-md border border-line px-1.5 py-0.5 text-xs text-ink-dim transition hover:border-brand/50"
              >
                {booking.loyaltyId}
              </Link>
            )}
            {late && <Pill tone="warning">{t('booking.late')}</Pill>}
          </div>

          {booking.vehicleNote && (
            <p className="mt-1 text-sm text-ink-dim">{booking.vehicleNote}</p>
          )}
          {booking.request && (
            <p className="mt-0.5 text-sm text-ink-dim">{booking.request}</p>
          )}
          {booking.assigneeName && (
            <p className="mt-1 text-xs text-ink-faint">{t('booking.forMaster', { name: booking.assigneeName })}</p>
          )}
          {booking.phone && (
            <a
              href={`tel:${booking.phone}`}
              className="tabular -mb-1 mt-0.5 inline-flex min-h-9 items-center text-xs text-ink-faint transition hover:text-brand"
            >
              {booking.phone}
            </a>
          )}
        </div>

        <span className={`tabular text-right text-xs ${late ? 'text-warning' : 'text-ink-faint'}`}>
          {formatWhen(booking.expectedAt)}
          <span className="block text-[0.68rem] text-ink-faint">{durationLabel(booking.durationMinutes)}</span>
        </span>
      </div>

      {mayWrite && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            className="flex-1"
            onClick={() => navigate(
              // A regular goes straight to intake; a stranger stops at the
              // customer picker first, which is the only thing still missing.
              booking.customerId
                ? `/floor/accept?customer=${booking.customerId}&booking=${booking.id}`
                : `/floor/accept?booking=${booking.id}`,
            )}
          >
            {t('booking.arrived')}
          </Button>
          <Button variant="ghost" onClick={() => setEditing(true)}>{t('common.edit')}</Button>
          <Button
            variant="quiet"
            loading={close.isPending}
            onClick={() => close.mutate({ id: booking.id, how: 'no-show' })}
          >
            {t('booking.noShow')}
          </Button>
          <Button
            variant="quiet"
            loading={close.isPending}
            onClick={() => close.mutate({ id: booking.id, how: 'cancel' })}
          >
            {t('booking.cancel')}
          </Button>
          <EraseButton compact path={`bookings/${booking.id}`} name={booking.name} />
        </div>
      )}
    </Card>
  );
}

/** Who is expected, and the form to add one more. */
export function BookingList() {
  const { t } = useI18n();
  const { data: session } = useSession();
  const { data, isPending, error } = useBookings('open');
  const [adding, setAdding] = useState(false);

  const mayWrite = can(session, 'bookings.write');
  const items = data?.items ?? [];

  return (
    <div className="space-y-3">
      {error && <Alert>{t('app.apiUnreachable')}</Alert>}

      {mayWrite && (adding
        ? <BookingCard onDone={() => setAdding(false)} />
        : <Button className="w-full" onClick={() => setAdding(true)}>{t('booking.new')}</Button>
      )}

      {isPending ? (
        <Skeleton className="h-32" />
      ) : items.length === 0 ? (
        <EmptyState title={t('booking.empty')} />
      ) : (
        items.map((booking) => (
          <Row key={booking.id} booking={booking} mayWrite={mayWrite} />
        ))
      )}
    </div>
  );
}
